// Home › The month so far, Blueprint only (pages/ux-full #/home/numbers), on DashboardPage (BL-189): the
// month's figures under the title block, the pace beside the targets, then each day. The pace is ui-web's
// GoalPace, drawn from the huddle's own answer (it carries every field gp.pace does); the owner's goals
// open in place of the targets. ‹ month › steps through the months. Riverstone's huddle stays in
// huddle/Huddle.tsx, which hands Blueprint companies here.
import { useMemo, useState } from 'react';
import { viewFor } from '@flowology/ui-web';
import { Button, DataTable, Figures, KeyValue, Link, Segmented, TextField } from '@flowology/ui-web/fl';
import type { DataCell, DataColumn } from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useAct, useQuery, useToast } from '../books/ui.tsx';
import { money, monthName, monthOf, todayIn } from '../books/model.ts';
import {
  BASIS_WORDS,
  COUNTING,
  dayLabel,
  GOAL_FIELDS,
  goalInput,
  goalText,
  huddleFigures,
  mayEditGoals,
  paceNoteList,
  paceWords,
  stepMonth,
  type GoalField,
  type Huddle,
  type HuddleDay,
} from '../huddle/huddle-model.ts';
import { officeHost } from './host.ts';
import { pageLabel } from './places.ts';
import './huddle-page.css';

type Run = ReturnType<typeof useAct>['run'];

export function HuddlePage({ nav, held }: { nav: NavProps; held: readonly string[] }) {
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [[from, to], setRange] = useState(monthOf(todayIn(tz)));
  const [goals, setGoals] = useState(false);
  const [toast, setToast] = useToast();
  const h = useQuery<Huddle>('gp.huddle', { from, to });
  const act = useAct(setToast, () => h.reload());
  const crumb = usePageCrumb(nav);
  // the pace draws no action in the office, so nothing here opens a place
  const host = useMemo(() => officeHost(() => undefined), []);
  const thisMonth = monthOf(todayIn(tz))[0] === from;
  const month = monthName(from).split(' ')[0];
  const editing = goals && mayEditGoals(held);
  const step = (by: 1 | -1) => monthName(stepMonth(from, by)[0]).split(' ')[0];
  const actions = (
    <>
      <Segmented
        selectionMode="none"
        label="Month"
        items={[
          { id: 'before', label: `‹ ${step(-1)}` },
          { id: 'this', label: 'This month', isDisabled: thisMonth },
          { id: 'after', label: `${step(1)} ›` },
        ]}
        onAction={(id) =>
          setRange(id === 'this' ? monthOf(todayIn(tz)) : stepMonth(from, id === 'before' ? -1 : 1))
        }
      />
      {mayEditGoals(held) && (
        <Button aria-expanded={goals} onPress={() => setGoals((g) => !g)}>
          {goals ? 'Close the goals' : `${month}’s goals`}
        </Button>
      )}
    </>
  );
  const form = (
    <Goals month={from.slice(0, 7)} busy={act.busy} run={act.run} onDone={() => setGoals(false)} />
  );
  const goalsPart: DashboardSection = { id: 'goals', title: `${month}’s goals`, children: form };
  const data = h.data;
  // while the huddle loads the page says so once, unless the goals are open: then they stay, beside it
  const waiting =
    data || editing
      ? undefined
      : {
          status: h.error ? ('failed' as const) : ('loading' as const),
          what: 'the huddle',
          ...(h.error ? { failedText: h.error } : {}),
          onRetry: () => void h.reload(),
        };
  const pace: DashboardSection = data
    ? {
        id: 'pace',
        title: `${month} against the goal`,
        children: (
          <>
            {viewFor('gp.pace', data, host) ?? <p>{paceWords(data).line}</p>}
            {paceNoteList(data)
              .filter((n) => n.key !== 'heading' && n.key !== 'review')
              .map((n) => (
                <p key={n.key} className="bp-huddle-note">
                  {n.text}
                </p>
              ))}
          </>
        ),
      }
    : {
        id: 'pace',
        title: `${month} against the goal`,
        what: 'the huddle',
        status: h.error ? 'failed' : 'loading',
        ...(h.error ? { failedText: h.error } : {}),
        onRetry: () => void h.reload(),
      };
  const side: DashboardSection = editing
    ? goalsPart
    : { id: 'targets', title: 'Targets', children: data && <Targets h={data} /> };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: pageLabel('huddle'),
            lede: `${monthName(from)}${data ? `. ${BASIS_WORDS[data.basis]}` : ''}`,
            actions,
            ...(data ? { figures: <Figures items={huddleFigures(data)} /> } : {}),
          }}
          {...(waiting ? { waiting } : {})}
          main={[pace]}
          side={[side]}
          {...(data
            ? {
                below: [
                  {
                    id: 'days',
                    title: 'Each day',
                    children: <Days h={data} busy={act.busy} run={act.run} />,
                  },
                ],
                foot: `${COUNTING} ${data.jobCount} ${data.jobCount === 1 ? 'job' : 'jobs'} earned in ${monthName(from)}.`,
              }
            : {})}
          layers={
            toast && (
              <div className="toast" role="status">
                {toast}
              </div>
            )
          }
        />
      </main>
    </div>
  );
}

/** The targets and each working day's budget, as facts on hairlines sharing one label column. */
function Targets({ h }: { h: Huddle }) {
  const t = h.targets;
  const b = h.budgeted;
  const n = (v: number | null) => (v === null ? '—' : String(v));
  const cents = (v: number | null) => (v === null ? '—' : money(v));
  return (
    <KeyValue
      items={[
        { label: 'Working days', value: String(h.workingDays) },
        { label: 'Revenue goal', value: cents(t.revenueCents) },
        { label: 'Planned technicians', value: n(t.plannedTechs) },
        {
          label: 'Technicians working',
          value: `${t.actualTechs} ${t.actualTechsFrom === 'set' ? 'as set' : 'from the timesheets'}`,
        },
        { label: 'Calls a technician a day', value: n(t.callsPerTechDay) },
        { label: 'Calls this month', value: n(t.monthlyCalls) },
      ]}
      groups={[
        {
          head: 'Budgeted each working day',
          items: [
            { label: 'Revenue', value: cents(b.dailyRevenueCents) },
            { label: 'Calls', value: n(b.dailyCalls) },
            { label: 'Average ticket', value: cents(b.avgTicketCents) },
            { label: 'Conversion rate', value: b.conversionPct === null ? '—' : `${b.conversionPct} %` },
            { label: 'Average sale', value: cents(b.avgSaleCents) },
          ],
        },
      ]}
    />
  );
}

type DayRow = HuddleDay & { id: string };

/** Each day: an off day's figures muted, today's strong; a day to come has none yet. */
function Days({ h, busy, run }: { h: Huddle; busy: boolean; run: Run }) {
  const look = (d: HuddleDay): DataCell['look'] => (d.today ? 'strong' : !d.working ? 'muted' : undefined);
  const fig = (d: HuddleDay, v: string): DataCell => ({
    text: d.future ? '' : v,
    ...(look(d) ? { look: look(d) } : {}),
  });
  const columns: DataColumn<DayRow>[] = [
    {
      id: 'day',
      label: 'Day',
      rowHeader: true,
      cell: (d) => {
        const l = dayLabel(d.date);
        return {
          // the day's number is its figure; today is the current one, for a screen reader too
          text: (
            <span aria-current={d.today ? 'date' : undefined}>
              {l.weekday} <b>{l.num}</b>
            </span>
          ),
          ...(look(d) ? { look: look(d) } : {}),
          lines: [...(d.closed ? [{ text: d.closed }] : []), ...(d.today ? [{ text: 'Today' }] : [])],
        };
      },
    },
    {
      id: 'revenue',
      label: 'Revenue earned',
      align: 'end',
      cell: (d) => ({
        text: d.revenueCents ? money(d.revenueCents) : d.future ? '' : '—',
        ...(look(d) ? { look: look(d) } : {}),
        lines: [
          ...(d.provisionalCents > 0 ? [{ text: 'draft invoices' }] : []),
          ...(d.inProgressCents > 0 ? [{ text: `+ ${money(d.inProgressCents)} in progress` }] : []),
        ],
      }),
    },
    { id: 'jobs', label: 'Jobs', align: 'end', cell: (d) => fig(d, String(d.jobs)) },
    { id: 'sold', label: 'Sold', align: 'end', cell: (d) => fig(d, String(d.sold)) },
    {
      id: 'passed',
      label: 'Days passed',
      align: 'end',
      cell: (d) => ({ text: String(d.workingDaysPassed), look: 'muted' }),
    },
    { id: 'note', label: 'Note', cell: (d) => ({ text: <Note d={d} busy={busy} run={run} /> }) },
  ];
  return (
    <DataTable
      columns={columns}
      rows={h.days.map((d) => ({ ...d, id: d.date }))}
      empty="No days in this month."
      stickyRowHeader
    />
  );
}

/** A day's note: its underlined word, or a box while it's written; Enter or leaving saves, Escape doesn't. */
function Note({ d, busy, run }: { d: HuddleDay; busy: boolean; run: Run }) {
  const [text, setText] = useState<string | null>(null);
  const l = dayLabel(d.date);
  const label = `Note for ${l.weekday} ${l.num}`;
  if (text === null)
    return (
      <Link
        label={d.note ? `${label}: ${d.note}` : `Add a ${label.toLowerCase()}`}
        onPress={() => setText(d.note ?? '')}
      >
        {d.note ?? '+ Note'}
      </Link>
    );
  const save = async () => {
    const t = text.trim();
    setText(null);
    if (t === (d.note ?? '')) return;
    await run('gp.addHuddleNote', { day: d.date, text: t }, t ? 'Note saved.' : 'Note removed.');
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setText(null);
      }}
    >
      <TextField
        label={label}
        hideLabel
        value={text}
        maxLength={500}
        autoFocus
        isDisabled={busy}
        onChange={setText}
        onBlur={() => void save()}
      />
    </form>
  );
}

/** The month's goals (finance.targets.edit): empty clears a goal; a word that isn't a number is refused there. */
function Goals({ month, busy, run, onDone }: { month: string; busy: boolean; run: Run; onDone: () => void }) {
  const s = useQuery<{ value: ({ month: string } & Partial<Record<GoalField, number | null>>)[] }>(
    'settings.get',
    { key: 'huddle.targets' },
  );
  const [texts, setTexts] = useState<Record<GoalField, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const row = s.data?.value.find((r) => r.month === month);
  const shown =
    texts ??
    (Object.fromEntries(GOAL_FIELDS.map((f) => [f.key, goalText(f, row?.[f.key])])) as Record<
      GoalField,
      string
    >);
  if (!s.data)
    return s.error ? (
      <p role="alert" className="bp-huddle-note">
        Flowology couldn’t load the goals. {s.error}
      </p>
    ) : (
      <p className="bp-huddle-note">Loading the goals…</p>
    );
  // the refusal names its field ("Average ticket isn’t a number."), so it goes under that field
  const refused =
    error &&
    (GOAL_FIELDS.find((f) => error.startsWith(f.label))?.key ??
      (/conversion/i.test(error) ? 'conversionPct' : GOAL_FIELDS[0]!.key));
  return (
    <form
      className="bp-huddle-goals"
      onSubmit={(e) => {
        e.preventDefault();
        const r = goalInput(shown);
        if ('error' in r) return setError(r.error);
        setError(null);
        void run('gp.setTarget', { month, ...r.fields }, 'Goals saved.').then((done) => done && onDone());
      }}
    >
      {GOAL_FIELDS.map((f) => (
        <TextField
          key={f.key}
          label={f.label}
          inputMode="decimal"
          value={shown[f.key]}
          {...(f.money ? { placeholder: '$' } : {})}
          {...(f.hint ? { description: f.hint } : {})}
          error={refused === f.key ? error : null}
          onChange={(v) => setTexts({ ...shown, [f.key]: v })}
        />
      ))}
      <div>
        <Button variant="primary" type="submit" isDisabled={busy}>
          Save the goals
        </Button>
      </div>
    </form>
  );
}
